import PropTypes from 'prop-types';
import React from 'react';
import { Constants } from '@pwc/pwc-deals-web-common';
import { useSelector } from 'react-redux';
import { getLinkedAdjustmentsAsSourceForDdl } from '../../../../../common/engine/selectors/adjustmentLinkSelectors';
import {
  getNetDebtAndDebtLikeForDdl,
  getNetDebtForDdl,
  getTableReadyAdjustmentSumsNotParticipatingInCalculationsForDdl,
  getTableReadyBankDebtAdjustmentSumsForDdl,
  getTableReadyDebtLikeItemAdjustmentSumsForDdl,
  getTableReadyLessCashAdjustmentSumsForDdl,
} from '../../../../../common/engine/selectors/adjustmentSelectors';
import { getAdjustmentTogglesForDdl } from '../../../../../common/engine/selectors/adjustmentTogglesSelectors';
import { getTimePeriodsForDdl } from '../../../../../common/engine/selectors/timePeriodSelectors';
import {
  NET_DEBT_AND_DEBT_LIKE_KEY,
  NET_DEBT_KEY,
  OTHER_CONSIDERATIONS_TOTALS_OVERRIDE_TEXT,
} from '../../../../../common/engine/utilities/constants';
import AdjustmentGroupForMode from '../../common/AdjustmentGroup/AdjustmentGroupForMode';
import OverallTableHeader from '../../common/OverallTableHeader';
import ReportedFinancials from '../../common/ReportedFinancials';

const DebtDebtLikeTable = ({
  isClientView,
  contentVersionNumber,
  contentId,
  projectId,
  forUseWithPublishingSSRService,
  nameOfAdjustmentToExpand,
  targetAdjustmentType,
  targetAdjustmentName,
  isEditMode,
  adjustmentOptions,
  targetDocumentOptions,
  selectedLinkedAdjustments,
  setFieldValue,
  showLinkedAdjustments,
}) => {
  const ddlFinancialPeriods = useSelector(getTimePeriodsForDdl);
  const bankDebtAdjustmentSums = useSelector(
    getTableReadyBankDebtAdjustmentSumsForDdl
  );
  const lessCashAdjustmentSums = useSelector(
    getTableReadyLessCashAdjustmentSumsForDdl
  );
  const netDebt = useSelector(getNetDebtForDdl);
  const debtLikeItemAdjustmentSums = useSelector(
    getTableReadyDebtLikeItemAdjustmentSumsForDdl
  );
  const netDebtAndDebtLike = useSelector(getNetDebtAndDebtLikeForDdl);
  const notParticipatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsNotParticipatingInCalculationsForDdl
  );
  const ddlAdjustmentToggles = useSelector(getAdjustmentTogglesForDdl);
  const links = useSelector(getLinkedAdjustmentsAsSourceForDdl);
  const linkedAdjustments = links.filter(
    (link) => !isClientView || !link.isBroken
  );
  const isPublishMode = forUseWithPublishingSSRService;
  return (
    <table
      role="table"
      className="analysis-table"
      data-testid="structured-analysis-view-DDL"
      aria-label="Debt and Debt Like"
    >
      <thead>
        <OverallTableHeader
          timePeriods={ddlFinancialPeriods}
          isPublishMode={isPublishMode}
        />
      </thead>
      <tbody>
        {bankDebtAdjustmentSums.map((adjustmentTypeGroup) => (
          <AdjustmentGroupForMode
            projectId={projectId}
            documentCode={Constants.DEBT_AND_DEBT_LIKE}
            contentId={contentId}
            contentVersionNumber={contentVersionNumber}
            key={adjustmentTypeGroup.adjustmentType}
            group={adjustmentTypeGroup.adjustments}
            groupName={adjustmentTypeGroup.adjustmentType}
            timePeriods={ddlFinancialPeriods}
            linkedAdjustments={linkedAdjustments}
            adjustmentToggles={ddlAdjustmentToggles}
            targetAdjustmentType={targetAdjustmentType}
            targetAdjustmentName={targetAdjustmentName}
            nameOfAdjustmentToExpand={nameOfAdjustmentToExpand}
            isPublishMode={isPublishMode}
            isEditMode={isEditMode}
            adjustmentOptions={adjustmentOptions}
            targetDocumentOptions={targetDocumentOptions}
            selectedLinkedAdjustments={selectedLinkedAdjustments}
            setFieldValue={setFieldValue}
            showLinkedAdjustments={showLinkedAdjustments}
            isClientView={isClientView}
          />
        ))}
        {lessCashAdjustmentSums.map((adjustmentTypeGroup) => (
          <AdjustmentGroupForMode
            projectId={projectId}
            documentCode={Constants.DEBT_AND_DEBT_LIKE}
            contentId={contentId}
            contentVersionNumber={contentVersionNumber}
            key={adjustmentTypeGroup.adjustmentType}
            group={adjustmentTypeGroup.adjustments}
            groupName={adjustmentTypeGroup.adjustmentType}
            timePeriods={ddlFinancialPeriods}
            linkedAdjustments={linkedAdjustments}
            adjustmentToggles={ddlAdjustmentToggles}
            targetAdjustmentType={targetAdjustmentType}
            targetAdjustmentName={targetAdjustmentName}
            nameOfAdjustmentToExpand={nameOfAdjustmentToExpand}
            isPublishMode={isPublishMode}
            isEditMode={isEditMode}
            adjustmentOptions={adjustmentOptions}
            targetDocumentOptions={targetDocumentOptions}
            selectedLinkedAdjustments={selectedLinkedAdjustments}
            setFieldValue={setFieldValue}
            showLinkedAdjustments={showLinkedAdjustments}
            isClientView={isClientView}
          />
        ))}
        <ReportedFinancials
          rowText={NET_DEBT_KEY}
          financials={netDebt}
          timePeriods={ddlFinancialPeriods}
          testId="net-debt"
          className="net-debt"
          isPublishMode={isPublishMode}
        />
        {debtLikeItemAdjustmentSums.map((adjustmentTypeGroup) => (
          <AdjustmentGroupForMode
            projectId={projectId}
            documentCode={Constants.DEBT_AND_DEBT_LIKE}
            contentId={contentId}
            contentVersionNumber={contentVersionNumber}
            key={adjustmentTypeGroup.adjustmentType}
            group={adjustmentTypeGroup.adjustments}
            groupName={adjustmentTypeGroup.adjustmentType}
            timePeriods={ddlFinancialPeriods}
            linkedAdjustments={linkedAdjustments}
            adjustmentToggles={ddlAdjustmentToggles}
            targetAdjustmentType={targetAdjustmentType}
            targetAdjustmentName={targetAdjustmentName}
            adjustmentOptions={adjustmentOptions}
            targetDocumentOptions={targetDocumentOptions}
            nameOfAdjustmentToExpand={nameOfAdjustmentToExpand}
            selectedLinkedAdjustments={selectedLinkedAdjustments}
            setFieldValue={setFieldValue}
            isPublishMode={isPublishMode}
            isEditMode={isEditMode}
            showLinkedAdjustments={showLinkedAdjustments}
            isClientView={isClientView}
          />
        ))}
        <ReportedFinancials
          rowText={NET_DEBT_AND_DEBT_LIKE_KEY}
          financials={netDebtAndDebtLike}
          timePeriods={ddlFinancialPeriods}
          testId="net-debt-and-debt-like"
          className="net-debt"
          isPublishMode={isPublishMode}
        />
        {notParticipatingAdjustmentSums.map((adjustmentTypeGroup) => (
          <AdjustmentGroupForMode
            projectId={projectId}
            documentCode={Constants.DEBT_AND_DEBT_LIKE}
            contentId={contentId}
            contentVersionNumber={contentVersionNumber}
            key={adjustmentTypeGroup.adjustmentType}
            group={adjustmentTypeGroup.adjustments}
            groupName={adjustmentTypeGroup.adjustmentType}
            timePeriods={ddlFinancialPeriods}
            adjustmentToggles={ddlAdjustmentToggles}
            adjustmentOptions={adjustmentOptions}
            totalOverrideValue={OTHER_CONSIDERATIONS_TOTALS_OVERRIDE_TEXT}
            showToggles={false}
            showTotalRow={false}
            nameOfAdjustmentToExpand={nameOfAdjustmentToExpand}
            isPublishMode={isPublishMode}
            isEditMode={isEditMode}
            setFieldValue={setFieldValue}
            isClientView={isClientView}
          />
        ))}
      </tbody>
    </table>
  );
};

DebtDebtLikeTable.propTypes = {
  isClientView: PropTypes.bool,
  contentId: PropTypes.number.isRequired,
  contentVersionNumber: PropTypes.number.isRequired,
  projectId: PropTypes.string.isRequired,
  targetAdjustmentType: PropTypes.string,
  targetAdjustmentName: PropTypes.string,
  nameOfAdjustmentToExpand: PropTypes.string,
  forUseWithPublishingSSRService: PropTypes.bool,
};

DebtDebtLikeTable.defaultProps = {
  isClientView: false,
  nameOfAdjustmentToExpand: null,
  targetAdjustmentType: null,
  targetAdjustmentName: null,
  forUseWithPublishingSSRService: false,
};

export default DebtDebtLikeTable;
